import React, { useState, useEffect, useContext } from "react";
import { PageLayout } from "../components/page-layout";
import { LeaderboardContext } from "../contexts/leaderboard-context";
import { getScoreBreakdownResource } from "../services/score-breakdown.service";
import { Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Paper } from '@mui/material';
import Skeleton from '@mui/material/Skeleton';
import Stack from '@mui/material/Stack';
import { useSiteVarsFunctions } from "../hooks/use-site-vars-functions";

export const ScoreBreakdownPage = () => {
  const { leaderboardData } = useContext(LeaderboardContext);
  const [selectedClubId, setSelectedClubId] = useState("");
  const [scoreBreakdownData, setScoreBreakdownData] = useState(null);
  const { getTeamById, getPlayerPositionById } = useSiteVarsFunctions();

  useEffect(() => {
    if (selectedClubId) {
      fetchScoreBreakdownData(selectedClubId);
    }
  }, [selectedClubId]);

  const fetchScoreBreakdownData = async (clubId) => {
    setScoreBreakdownData(null); // Clear previous data
    const { data, error } = await getScoreBreakdownResource(clubId);
    if (data) {
      setScoreBreakdownData(data);
    } else if (error) {
      console.error("Error fetching score breakdown data:", error);
      setScoreBreakdownData(null);
    }
  };

  const handleClubChange = (event) => {
    setSelectedClubId(event.target.value);
  };

  const getWeeks = () => {
    if (!scoreBreakdownData || !scoreBreakdownData.players || scoreBreakdownData.players.length === 0) {
      return [];
    }
    const player = scoreBreakdownData.players[0];
    return Object.keys(player)
      .filter(key => key.startsWith("week"))
      .sort((a, b) => parseInt(a.replace('week', '')) - parseInt(b.replace('week', '')));
  };

  const calculateWeeklyTotals = (weeks) => {
    if (!scoreBreakdownData || !scoreBreakdownData.players || !scoreBreakdownData.teams) {
      return {};
    }
    const totals = {};
    weeks.forEach(week => {
      const weekNumber = parseInt(week.replace('week', ''));
      const playersForWeek = scoreBreakdownData.teams
        .filter(teamEntry => teamEntry.week === weekNumber && teamEntry.clubId === parseInt(selectedClubId))
        .map(teamEntry => teamEntry.playerId);

      totals[week] = scoreBreakdownData.players
        .filter(player => {
          const teamEntry = scoreBreakdownData.teams.find(entry =>
            entry.week === weekNumber &&
            entry.clubId === parseInt(selectedClubId) &&
            entry.playerId === player.playerId
          );
          return teamEntry && teamEntry.transfer !== "out";
        })
        .reduce((sum, player) => sum + (player[week] || 0), 0);
    });
    return totals;
  };

  const weeks = getWeeks();
  const weeklyTotals = calculateWeeklyTotals(weeks);

  return (
    <PageLayout>
      <Paper sx={{ padding: 1, marginTop: 1 }}>
        <div className="content-layout">
          <div className="content__body">
            <label htmlFor="team-select">Select Team:</label>
            <select id="team-select" onChange={handleClubChange} value={selectedClubId}>
              <option value="">--Please choose a team--</option>
              {leaderboardData.map((team) => (
                <option key={team.clubId} value={team.clubId}>
                  {team.managerName} - {team.clubName}
                </option>
              ))}
            </select>

            {selectedClubId && !scoreBreakdownData && (
              <TableContainer component={Paper} sx={{ mt: 2 }}>
                <Stack spacing={1}>
                  <Skeleton variant="rounded" width={"100%"} height={30} />
                  <Skeleton variant="rounded" width={"100%"} height={45} />
                  <Skeleton variant="rounded" width={"100%"} height={45} />
                  <Skeleton variant="rounded" width={"100%"} height={45} />
                  <Skeleton variant="rounded" width={"100%"} height={45} />
                </Stack>
              </TableContainer>
            )}

            {scoreBreakdownData && weeks.map(week => (
              <TableContainer component={Paper} sx={{ mt: 2 }} key={week}>
                <h3>{week.replace('week', 'Week ')} Scores for {leaderboardData.find(team => team.clubId == selectedClubId)?.clubName}</h3>
                <Table sx={{ minWidth: 350 }} size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>Player</TableCell>
                      <TableCell>Position</TableCell>
                      <TableCell>Score</TableCell>
                      <TableCell>Loan</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {scoreBreakdownData.players
                      .filter(player => scoreBreakdownData.teams.some(teamEntry =>
                        teamEntry.week === parseInt(week.replace('week', '')) &&
                        teamEntry.clubId === parseInt(selectedClubId) &&
                        teamEntry.playerId === player.playerId
                      ))
                      .sort((a, b) => {
                        let positionOrder = a.positionId - b.positionId;
                        let nameOrder = a.lastName.localeCompare(b.lastName);
                        return positionOrder || nameOrder;
                      })
                      .map((player) => (
                        <TableRow key={player.playerId}>
                          <TableCell>{player.lastName}, {player.firstName}</TableCell>
                          <TableCell>{getPlayerPositionById(player.positionId).name_short}</TableCell>
                          <TableCell>{player[week] || 0}</TableCell>
                          <TableCell>
                            {scoreBreakdownData.teams.find(teamEntry =>
                              teamEntry.week === parseInt(week.replace('week', '')) &&
                              teamEntry.clubId === parseInt(selectedClubId) &&
                              teamEntry.playerId === player.playerId
                            )?.transfer || ''}
                          </TableCell>
                        </TableRow>
                      ))}
                    <TableRow>
                      <TableCell colSpan={3} sx={{ fontWeight: 'bold' }}>Weekly Total</TableCell>
                      <TableCell sx={{ fontWeight: 'bold' }}>{weeklyTotals[week]}</TableCell>
                    </TableRow>
                  </TableBody>
                </Table>
              </TableContainer>
            ))}
          </div>
        </div>
      </Paper>
    </PageLayout>
  );
};